import { useEffect, useState } from "react";
import { Loader2, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useSite } from "@/lib/site";
import { AssetPicker } from "./AssetPicker";

const FIELDS = [
  { name: "whatsapp_number", label: "WhatsApp number (with country code)" },
  { name: "corporate_email", label: "Corporate enquiries email" },
  { name: "email", label: "General contact email" },
  { name: "phone_display", label: "Phone (display text)" },
  { name: "instagram_handle", label: "Instagram handle" },
  { name: "instagram_url", label: "Instagram profile URL" },
  { name: "location_text", label: "Visit / location text (footer)" },
  { name: "propract_slug", label: "ProPract public slug" },
  { name: "hero_image_url", label: "Hero photo (homepage banner)", type: "image" },
  { name: "about_image_url", label: "About section photo", type: "image" },
  { name: "hero_eyebrow", label: "Hero eyebrow text" },
  { name: "hero_line1", label: "Hero headline — line 1" },
  { name: "hero_line2", label: "Hero headline — line 2 (italic accent)" },
  { name: "hero_line3", label: "Hero headline — line 3" },
  { name: "hero_subtext", label: "Hero subtext", type: "textarea" },
  { name: "about_eyebrow", label: "About section eyebrow" },
  { name: "about_heading", label: "About section heading" },
];

const SettingsField = ({ f, data, setData }) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  return (
    <div>
      <label className="mb-1 block font-outfit text-xs font-semibold uppercase tracking-wider text-faint">
        {f.label}
      </label>
      <div className="flex gap-2">
        {f.type === "textarea" ? (
          <textarea
            data-testid={`admin-settings-${f.name}`}
            rows={3}
            value={data[f.name] || ""}
            onChange={(e) => setData({ ...data, [f.name]: e.target.value })}
            className="w-full rounded-xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-terra"
          />
        ) : (
          <input
            data-testid={`admin-settings-${f.name}`}
            value={data[f.name] || ""}
            onChange={(e) => setData({ ...data, [f.name]: e.target.value })}
            className="w-full rounded-xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-terra"
          />
        )}
        {f.type === "image" && (
          <button
            type="button"
            data-testid={`admin-settings-${f.name}-library`}
            onClick={() => setPickerOpen(true)}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-xl border border-ink/15 bg-white px-4 text-xs font-semibold text-ink hover:border-terra hover:text-terra"
          >
            <ImageIcon className="h-4 w-4" /> Library
          </button>
        )}
      </div>
      {f.type === "image" && data[f.name] && (
        <img src={data[f.name]} alt="" className="mt-2 h-16 w-16 rounded-lg object-cover" />
      )}
      {f.type === "image" && (
        <AssetPicker open={pickerOpen} onClose={() => setPickerOpen(false)} onSelect={(url) => setData({ ...data, [f.name]: url })} />
      )}
    </div>
  );
};

export const SettingsTab = () => {
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(false);
  const { refresh } = useSite();

  useEffect(() => {
    api.get("/admin/settings").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", data);
      toast.success("Settings saved");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-tab-settings" className="max-w-2xl">
      <h2 className="mb-2 font-display text-2xl font-semibold text-ink">Site settings</h2>
      <p className="mb-6 text-sm text-faint">
        Contact & connect details power the WhatsApp button, corporate section and footer.
      </p>
      <div className="space-y-4">
        {FIELDS.map((f) => (
          <SettingsField key={f.name} f={f} data={data} setData={setData} />
        ))}
      </div>
      <button
        data-testid="admin-settings-save"
        onClick={save}
        disabled={saving}
        className="mt-6 inline-flex items-center gap-2 rounded-full bg-terra px-6 py-3 font-outfit text-sm font-semibold text-white hover:bg-terradark disabled:opacity-60"
      >
        {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save settings
      </button>
    </div>
  );
};
